import React, { useState, useEffect } from 'react'
import { 
  ArrowLeft,
  Save,
  RotateCcw,
  Calendar
} from 'lucide-react'
import Breadcrumb from '../../components/Common/Breadcrumb'
import { useNavigate, useLocation } from 'react-router-dom'
import { toast } from 'react-toastify'
import contractRenewalService from '../../services/contractRenewalService'
import signedContractService from '../../services/signedContractService'

const ContractRenewalForm = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const isEditMode = location.pathname.includes('/edit')
  const isViewMode = location.pathname.includes('/view')
  const renewalData = location.state?.renewalData
  const originalContractId = location.state?.originalContractId

  const [formData, setFormData] = useState({
    renewalNumber: '',
    originalContract: '',
    renewalType: 'Renewal',
    renewalDate: new Date().toISOString().split('T')[0],
    renewalStartDate: '',
    renewalEndDate: '',
    renewalDuration: 0,
    renewalTerms: '',
    renewalValue: 0,
    currency: 'INR',
    approvalRequired: true,
    renewalNoticeDate: '',
    renewalNoticePeriod: 30,
    isAutoRenewal: false,
    renewalReason: '',
    notes: '',
    internalNotes: '',
    status: 'Draft'
  })

  const [signedContracts, setSignedContracts] = useState([])
  const [originalContractData, setOriginalContractData] = useState(null)
  const [isLoadingRenewalNumber, setIsLoadingRenewalNumber] = useState(true)
  const [isLoading, setIsLoading] = useState(false)

  // Fetch next renewal number (only for new renewals)
  useEffect(() => {
    if (!isEditMode && !isViewMode) {
      const fetchRenewalNumber = async () => {
        try {
          setIsLoadingRenewalNumber(true)
          const response = await contractRenewalService.getNextRenewalNumber()
          if (response.success && response.data) {
            setFormData(prev => ({
              ...prev,
              renewalNumber: response.data.renewalNumber || response.data.formattedValue || 'CRN0001'
            }))
          }
        } catch (error) {
          console.error('Error fetching renewal number:', error)
          setFormData(prev => ({ ...prev, renewalNumber: 'CRN0001' }))
        } finally {
          setIsLoadingRenewalNumber(false)
        }
      }
      fetchRenewalNumber()
    }
  }, [isEditMode, isViewMode])

  // Load signed contracts
  useEffect(() => {
    loadSignedContracts()
  }, [])

  // Load renewal data if editing or viewing
  useEffect(() => {
    if ((isEditMode || isViewMode) && renewalData?._id) {
      loadRenewalData(renewalData._id)
    } else if (originalContractId) {
      loadOriginalContract(originalContractId)
    }
  }, [isEditMode, isViewMode, renewalData, originalContractId])

  const loadSignedContracts = async () => {
    try {
      const response = await signedContractService.getAllSignedContracts({ 
        status: 'Active',
        limit: 1000 
      })
      if (response.success) {
        setSignedContracts(response.data || [])
      }
    } catch (error) {
      console.error('Error loading signed contracts:', error)
    }
  }

  const loadOriginalContract = async (id) => {
    try {
      setIsLoading(true)
      const response = await signedContractService.getSignedContractById(id)
      if (response.success && response.data) {
        const contract = response.data
        setOriginalContractData(contract)
        
        // Auto-populate fields from original contract
        const originalEndDate = contract.contractEndDate ? new Date(contract.contractEndDate) : new Date()
        const renewalStartDate = new Date(originalEndDate)
        renewalStartDate.setDate(renewalStartDate.getDate() + 1) // Start day after original ends

        setFormData(prev => ({
          ...prev,
          originalContract: contract._id,
          renewalValue: contract.contractValue || prev.renewalValue,
          currency: contract.currency || prev.currency,
          renewalStartDate: renewalStartDate.toISOString().split('T')[0]
        }))
      }
    } catch (error) {
      console.error('Error loading original contract:', error)
      toast.error('Failed to load original contract data')
    } finally {
      setIsLoading(false)
    }
  }

  const loadRenewalData = async (id) => {
    try {
      setIsLoading(true)
      const response = await contractRenewalService.getContractRenewalById(id)
      if (response.success && response.data) {
        const renewal = response.data
        setOriginalContractData(renewal.originalContract)
        setFormData({
          renewalNumber: renewal.renewalNumber || '',
          originalContract: renewal.originalContract?._id || renewal.originalContract || '',
          renewalType: renewal.renewalType || 'Renewal',
          renewalDate: renewal.renewalDate ? new Date(renewal.renewalDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
          renewalStartDate: renewal.renewalStartDate ? new Date(renewal.renewalStartDate).toISOString().split('T')[0] : '',
          renewalEndDate: renewal.renewalEndDate ? new Date(renewal.renewalEndDate).toISOString().split('T')[0] : '',
          renewalDuration: renewal.renewalDuration || 0,
          renewalTerms: renewal.renewalTerms || '',
          renewalValue: renewal.renewalValue || 0,
          currency: renewal.currency || 'INR',
          approvalRequired: renewal.approvalRequired !== undefined ? renewal.approvalRequired : true,
          renewalNoticeDate: renewal.renewalNoticeDate ? new Date(renewal.renewalNoticeDate).toISOString().split('T')[0] : '',
          renewalNoticePeriod: renewal.renewalNoticePeriod || 30,
          isAutoRenewal: renewal.isAutoRenewal || false,
          renewalReason: renewal.renewalReason || '',
          notes: renewal.notes || '',
          internalNotes: renewal.internalNotes || '',
          status: renewal.status || 'Draft'
        })
      }
    } catch (error) {
      console.error('Error loading renewal data:', error)
      toast.error('Failed to load renewal data')
    } finally {
      setIsLoading(false)
    }
  }

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target
    
    if (name === 'originalContract') {
      loadOriginalContract(value)
    } else if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }))
    } else if (type === 'number') {
      setFormData(prev => ({
        ...prev,
        [name]: parseFloat(value) || 0
      }))
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }))
    }

    // Calculate duration if dates changed
    if (name === 'renewalStartDate' || name === 'renewalEndDate') {
      const startDate = name === 'renewalStartDate' ? value : formData.renewalStartDate
      const endDate = name === 'renewalEndDate' ? value : formData.renewalEndDate
      if (startDate && endDate) {
        const start = new Date(startDate)
        const end = new Date(endDate)
        const diffTime = Math.abs(end - start)
        const diffMonths = Math.ceil(diffTime / (1000 * 60 * 60 * 24 * 30))
        setFormData(prev => ({
          ...prev,
          renewalDuration: diffMonths
        }))
      }
    }
  }

  const handleSave = async () => {
    try {
      // Validation
      if (!formData.originalContract || !formData.renewalStartDate) {
        toast.error('Please fill in all required fields (Original Contract and Renewal Start Date)')
        return
      }

      setIsLoading(true)
      
      if (isEditMode && renewalData?._id) {
        const response = await contractRenewalService.updateContractRenewal(renewalData._id, formData)
        if (response.success) {
          toast.success('Contract renewal updated successfully!')
          navigate('/contracts/contract-renewal')
        } else {
          toast.error(response.message || 'Failed to update contract renewal')
        }
      } else {
        const response = await contractRenewalService.createContractRenewal(formData)
        if (response.success) {
          toast.success('Contract renewal created successfully!')
          navigate('/contracts/contract-renewal')
        } else {
          toast.error(response.message || 'Failed to create contract renewal')
        }
      }
    } catch (error) {
      console.error('Error saving contract renewal:', error)
      if (error.response?.status === 400) {
        toast.error(error.response.data?.message || 'Invalid data provided')
      } else {
        if (!error._errorHandled) {
          toast.error('Failed to save contract renewal')
        }
      }
    } finally {
      setIsLoading(false)
    }
  }

  const handleReset = () => {
    if (isEditMode || isViewMode) {
      loadRenewalData(renewalData._id)
    } else if (originalContractId) {
      loadOriginalContract(originalContractId)
    } else {
      setFormData(prev => ({
        ...prev,
        originalContract: '',
        renewalType: 'Renewal',
        renewalDate: new Date().toISOString().split('T')[0],
        renewalStartDate: '',
        renewalEndDate: '',
        renewalDuration: 0,
        renewalTerms: '',
        renewalValue: 0,
        approvalRequired: true,
        renewalNoticeDate: '',
        renewalNoticePeriod: 30,
        isAutoRenewal: false,
        renewalReason: '',
        notes: '',
        internalNotes: '',
        status: 'Draft'
      }))
      setOriginalContractData(null)
    }
  }

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'Contracts', path: '/contracts' },
    { label: 'Contract Renewals', path: '/contracts/contract-renewal' },
    { 
      label: isEditMode ? 'Edit Contract Renewal' : isViewMode ? 'View Contract Renewal' : 'New Contract Renewal', 
      path: location.pathname, 
      isCurrent: true 
    }
  ]

  if (isLoading && (isEditMode || isViewMode || originalContractId)) {
    return (
      <div className="sales-dashboard">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <div className="spinner-border text-primary" role="status">
            <span className="sr-only">Loading...</span>
          </div>
          <p style={{ marginTop: '16px', color: 'var(--color-text-secondary, #5B6478)' }}>Loading renewal...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="sales-dashboard">
      <Breadcrumb items={breadcrumbItems} />

      <div className="page-header">
        <div className="page-title">
          <h1>{isEditMode ? 'Edit Contract Renewal' : isViewMode ? 'View Contract Renewal' : 'New Contract Renewal'}</h1>
          <p>{isEditMode ? 'Update contract renewal details' : isViewMode ? 'View contract renewal details' : 'Create a new contract renewal'}</p>
        </div>
        <div className="page-actions">
          <button 
            className="btn btn-secondary" 
            onClick={() => navigate('/contracts/contract-renewal')}
            style={{
              padding: '10px 20px',
              border: '1px solid #e9ecef',
              borderRadius: '6px',
              background: '#fff',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontFamily: 'Inter, sans-serif',
              fontSize: '14px',
              color: '#495057'
            }}
          >
            <ArrowLeft size={16} />
            Back
          </button>
        </div>
      </div>

      <div className="form-container">
        <div className="sac-form-header">
          <h2>Renewal Information</h2>
        </div>
          
        <form className="form-content">
          <div className="section-header"><h3>Basic Information</h3></div>
          {/* Row 1: Renewal Number, Original Contract, Renewal Type, Status */}
          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="renewalNumber" className="form-label required">
                Renewal Number
              </label>
              <input
                type="text"
                id="renewalNumber"
                name="renewalNumber"
                value={isLoadingRenewalNumber ? 'Generating...' : formData.renewalNumber}
                readOnly
                className="form-input"
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)', cursor: 'not-allowed' }}
              />
            </div>

            <div className="form-group">
              <label htmlFor="originalContract" className="form-label required">
                Original Contract
              </label>
              <select
                id="originalContract"
                name="originalContract"
                value={formData.originalContract}
                onChange={handleInputChange}
                disabled={isViewMode || isEditMode}
                className="form-input"
                required
              >
                <option value="">Select Original Contract</option>
                {signedContracts.map(contract => (
                  <option key={contract._id} value={contract._id}>
                    {contract.contractNumber} - {contract.contractTitle}
                    {contract.contractEndDate && ` (Ends: ${new Date(contract.contractEndDate).toLocaleDateString()})`}
                  </option>
                ))}
              </select>
              {originalContractData && (
                <small style={{ color: 'var(--color-text-secondary, #5B6478)', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                  Original Contract Value: {new Intl.NumberFormat('en-IN', { style: 'currency', currency: originalContractData.currency || 'INR' }).format(originalContractData.contractValue || 0)}
                  {originalContractData.contractEndDate && ` | Ends: ${new Date(originalContractData.contractEndDate).toLocaleDateString()}`}
                </small>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="renewalType" className="form-label">
                Renewal Type
              </label>
              <select
                id="renewalType"
                name="renewalType"
                value={formData.renewalType}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="Renewal">Renewal</option>
                <option value="Extension">Extension</option>
                <option value="Amendment">Amendment</option>
                <option value="Addendum">Addendum</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="status" className="form-label">
                Status
              </label>
              <select
                id="status"
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="Draft">Draft</option>
                <option value="Pending Approval">Pending Approval</option>
                <option value="Approved">Approved</option>
                <option value="Rejected">Rejected</option>
                <option value="Active">Active</option>
                <option value="Expired">Expired</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
          </div>

          {/* Renewal Dates */}
          <div className="section-header"><h3>Renewal Period</h3></div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="renewalDate" className="form-label">
                Renewal Date
              </label>
              <input
                type="date"
                id="renewalDate"
                name="renewalDate"
                value={formData.renewalDate}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="renewalStartDate" className="form-label required">
                Renewal Start Date
              </label>
              <input
                type="date"
                id="renewalStartDate"
                name="renewalStartDate"
                value={formData.renewalStartDate}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                required
              />
              {originalContractData?.contractEndDate && (
                <small style={{ color: 'var(--color-text-secondary, #5B6478)', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                  Original contract ends: {new Date(originalContractData.contractEndDate).toLocaleDateString()}
                </small>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="renewalEndDate" className="form-label">
                Renewal End Date
              </label>
              <input
                type="date"
                id="renewalEndDate"
                name="renewalEndDate"
                value={formData.renewalEndDate}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="renewalDuration" className="form-label">
                Duration (Months)
              </label>
              <input
                type="number"
                id="renewalDuration"
                name="renewalDuration"
                value={formData.renewalDuration}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="0"
                readOnly
                style={{ backgroundColor: '#f9fafb', color: 'var(--color-text-secondary, #5B6478)' }}
              />
            </div>
          </div>

          {/* Financial Information */}
          <div className="section-header"><h3>Financial Information</h3></div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="renewalValue" className="form-label">
                Renewal Value
              </label>
              <input
                type="number"
                id="renewalValue"
                name="renewalValue"
                value={formData.renewalValue}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="0"
                step="0.01"
              />
              {originalContractData && (
                <small style={{ color: 'var(--color-text-secondary, #5B6478)', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                  Original: {new Intl.NumberFormat('en-IN', { style: 'currency', currency: originalContractData.currency || 'INR' }).format(originalContractData.contractValue || 0)}
                </small>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="currency" className="form-label">
                Currency
              </label>
              <select
                id="currency"
                name="currency"
                value={formData.currency}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              >
                <option value="INR">INR</option>
                <option value="USD">USD</option>
                <option value="EUR">EUR</option>
                <option value="GBP">GBP</option>
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="approvalRequired" className="form-label">
                Approval Required
              </label>
              <div style={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}>
                <input
                  type="checkbox"
                  id="approvalRequired"
                  name="approvalRequired"
                  checked={formData.approvalRequired}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={{ width: '18px', height: '18px', cursor: isViewMode ? 'not-allowed' : 'pointer' }}
                />
                <label htmlFor="approvalRequired" style={{ marginLeft: '8px', fontSize: '14px', color: '#495057' }}>
                  Requires approval
                </label>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="isAutoRenewal" className="form-label">
                Auto Renewal
              </label>
              <div style={{ display: 'flex', alignItems: 'center', marginTop: '8px' }}>
                <input
                  type="checkbox"
                  id="isAutoRenewal"
                  name="isAutoRenewal"
                  checked={formData.isAutoRenewal}
                  onChange={handleInputChange}
                  disabled={isViewMode}
                  style={{ width: '18px', height: '18px', cursor: isViewMode ? 'not-allowed' : 'pointer' }}
                />
                <label htmlFor="isAutoRenewal" style={{ marginLeft: '8px', fontSize: '14px', color: '#495057' }}>
                  Auto-renew contract
                </label>
              </div>
            </div>
          </div>

          {/* Renewal Notice */}
          <div className="section-header"><h3>Renewal Notice</h3></div>

          <div className="grid-row-4">
            <div className="form-group">
              <label htmlFor="renewalNoticeDate" className="form-label">
                Notice Date
              </label>
              <input
                type="date"
                id="renewalNoticeDate"
                name="renewalNoticeDate"
                value={formData.renewalNoticeDate}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
              />
            </div>

            <div className="form-group">
              <label htmlFor="renewalNoticePeriod" className="form-label">
                Notice Period (Days)
              </label>
              <input
                type="number"
                id="renewalNoticePeriod"
                name="renewalNoticePeriod"
                value={formData.renewalNoticePeriod}
                onChange={handleInputChange}
                disabled={isViewMode}
                className="form-input"
                min="0"
              />
            </div>
          </div>

          {/* Renewal Terms and Reason */}
          <div className="form-group">
            <label htmlFor="renewalTerms" className="form-label">
              Renewal Terms
            </label>
            <textarea
              id="renewalTerms"
              name="renewalTerms"
              value={formData.renewalTerms}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              rows="3"
              placeholder="Describe the renewal terms and conditions..."
            />
          </div>

          <div className="form-group">
            <label htmlFor="renewalReason" className="form-label">
              Renewal Reason
            </label>
            <textarea
              id="renewalReason"
              name="renewalReason"
              value={formData.renewalReason}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              rows="2"
              placeholder="Reason for renewal..."
            />
          </div>

          {/* Notes */}
          <div className="form-group">
            <label htmlFor="notes" className="form-label">
              Notes
            </label>
            <textarea
              id="notes"
              name="notes"
              value={formData.notes}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              rows="3"
              placeholder="General notes..."
            />
          </div>

          <div className="form-group">
            <label htmlFor="internalNotes" className="form-label">
              Internal Notes
            </label>
            <textarea
              id="internalNotes"
              name="internalNotes"
              value={formData.internalNotes}
              onChange={handleInputChange}
              disabled={isViewMode}
              className="form-input"
              rows="3"
              placeholder="Internal notes..."
            />
          </div>

          {/* Action Buttons */}
          {!isViewMode && (
            <div className="form-actions">
              <button
                type="button"
                onClick={handleSave}
                disabled={isLoading}
                className="btn btn-primary"
                style={{
                  padding: '10px 24px',
                  background: '#292752',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  fontWeight: '500',
                  opacity: isLoading ? 0.6 : 1
                }}
              >
                <Save size={16} />
                {isLoading ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                onClick={handleReset}
                disabled={isLoading}
                className="btn btn-secondary"
                style={{
                  padding: '10px 24px',
                  border: '1px solid #e9ecef',
                  borderRadius: '6px',
                  background: '#fff',
                  cursor: isLoading ? 'not-allowed' : 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  fontFamily: 'Inter, sans-serif',
                  fontSize: '14px',
                  color: '#495057'
                }}
              >
                <RotateCcw size={16} />
                Reset
              </button>
            </div>
          )}
        </form>
      </div>
    </div>
  )
}

export default ContractRenewalForm

